import { Col, Row } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { defaultTo, get, isEmpty } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  FAMILY_SITUATION,
  SITUATION_PROFESSIONAL,
  SOCIAL_REGIMES,
} from "utils/constants";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import { filterContractNumber } from "utils/helpers/validations";
import ActionButtons from "../Components/ActionButtons";
import Actions from "../Components/Actions";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Switch from "../Components/Input/Switch";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

const MUTUAL_MOBILITY_SOCIAL_REGIMES = {
  [SOCIAL_REGIMES.SECURITE_SOCIALE]: SOCIAL_REGIMES.SECURITE_SOCIALE,
  [SOCIAL_REGIMES.ALSACE_MOSELLE]: SOCIAL_REGIMES.ALSACE_MOSELLE,
};

const MUTUAL_MOBILITY_FAMILY_SITUATION = [
  FAMILY_SITUATION.SINGLE,
  FAMILY_SITUATION.MARRIED,
  FAMILY_SITUATION.PACS,
  FAMILY_SITUATION.COHABITING,
];

const MUTUAL_MOBILITY_SITUATION_PROFESSIONAL = [
  SITUATION_PROFESSIONAL.SALARIED,
  SITUATION_PROFESSIONAL.STUDENT,
  SITUATION_PROFESSIONAL.RETIRED,
  SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB,
];

function View({ initialValues, locked, onNext, deltas, onBack }) {
  const [validStartDate, setValidStartDate] = useState(
    get(initialValues, "project.contract.issuanceDate")
      ? dayjs(get(initialValues, "project.contract.issuanceDate"))
      : dayjs().add(get(deltas, "start.normal"), "days"),
  );
  const [hasContract, setHasContract] = useState(
    defaultTo(get(initialValues, "project.ria"), false),
  );
  const { t } = useTranslation();
  const { t: f } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = f("B2C_MUTUAL_MOBILITY", {
    returnObjects: true,
  });
  const [pickerValue, setPickerValue] = useState(
    get(initialValues, "project.contract.issuanceDate")
      ? dayjs(get(initialValues, "project.contract.issuanceDate"))
      : dayjs().add(get(deltas, "start.normal"), "days"),
  );

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues,
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        deltaRia: get(deltas, "start.ria"),
        deltaStart: get(deltas, "start.normal"),
        deltaEndRia: get(deltas, "end.ria"),
        deltaEnd: get(deltas, "end.normal"),
      },
    ),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const RIA_STATUS = {
    [true]: () => {
      setValidStartDate(
        dayjs().add(get(deltas, "start.ria"), "days").startOf("day"),
      );
      setPickerValue(dayjs().add(get(deltas, "start.ria"), "days"));
      setFieldValue(
        "project.contract.issuanceDate",
        dayjs().add(get(deltas, "start.ria"), "days"),
      );
    },
    [false]: () => {
      setValidStartDate(
        dayjs().add(get(deltas, "start.normal"), "days").startOf("day"),
      );
      setPickerValue(
        dayjs().add(get(deltas, "start.normal"), "days").startOf("day"),
      );
      setFieldValue(
        "project.contract.issuanceDate",
        get(initialValues, "project.contract.issuanceDate") ||
          dayjs().add(get(deltas, "start.normal"), "days"),
      );
    },
  };
  return (
    <Layout>
      <Header
        title="Réponse immédiate ! Obtenez votre offre en moins de <b>2 minutes</b>"
        highlight="Réponse immédiate !"
        subtitle="Informations de l’adhérent"
        onBack={onBack}
      />
      <Row justify="center">
        <Col xs={24} md={16} lg={16}>
          <div className="grid--1">
            <div className="grid--2">
              <Input
                type="date"
                format={DATE_FORMAT}
                placeholder={"JJ / MM / AAAA"}
                label={"Date de naissance"}
                disabledDate={
                  (current) =>
                    current > dayjs().subtract(18, "years").endOf("year")
                  // || current < dayjs().subtract(75, "years").endOf("year")
                }
                defaultPickerValue={
                  !get(values, "project.contact.user.birthDate") &&
                  dayjs().subtract(30, "years")
                }
                {...bindDateInputProps({
                  name: "project.contact.user.birthDate",
                  setFieldValue,
                  values,
                  ...formProps,
                })}
                disabled={locked}
              />
              <Input
                placeholder={"ex : 69001"}
                maxLength={5}
                label={"Code postal"}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  values,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                    null,
                  );
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                    value,
                  );
                }}
                disabled={locked}
              />
            </div>
            <div className="grid--2">
              <Select
                options={MUTUAL_MOBILITY_FAMILY_SITUATION.map((value) => ({
                  value,
                  label: TRANSLATION.FORM.FAMILY_SITUATION.OPTIONS[value],
                }))}
                label={TRANSLATION.FORM.FAMILY_SITUATION.LABEL}
                placeholder={TRANSLATION.FORM.FAMILY_SITUATION.PLACEHOLDER}
                {...bindInputProps({
                  name: "project.contact.fields.familySituation",
                  values,
                  ...formProps,
                })}
                disabled={locked}
              />
              <Select
                options={MUTUAL_MOBILITY_SITUATION_PROFESSIONAL.map(
                  (value) => ({
                    value,
                    label:
                      TRANSLATION.FORM.SITUATION_PROFESSIONAL.OPTIONS[value],
                  }),
                )}
                label={TRANSLATION.FORM.SITUATION_PROFESSIONAL.LABEL}
                placeholder={
                  TRANSLATION.FORM.SITUATION_PROFESSIONAL.PLACEHOLDER
                }
                {...bindInputProps({
                  name: "project.contact.socioProfessionalCategory",
                  values,
                  ...formProps,
                })}
                disabled={locked}
              />
            </div>
            <div className="grid--2">
              <Select
                options={Object.entries(MUTUAL_MOBILITY_SOCIAL_REGIMES).map(
                  ([key, value]) => ({
                    value,
                    label: TRANSLATION.FORM.MANDATORY_SCHEME.OPTIONS[key],
                  }),
                )}
                label={TRANSLATION.FORM.MANDATORY_SCHEME.LABEL}
                placeholder={TRANSLATION.FORM.MANDATORY_SCHEME.PLACEHOLDER}
                {...bindInputProps({
                  name: "project.contact.socialRegime",
                  values,
                  ...formProps,
                })}
                disabled={locked}
              />
              <Input
                type="date"
                format={DATE_FORMAT}
                placeholder={"JJ / MM / AAAA"}
                label={"Date d’effet"}
                defaultPickerValue={pickerValue}
                disabledDate={(current) => {
                  return current && current < validStartDate;
                }}
                {...bindDateInputProps({
                  name: "project.contract.issuanceDate",
                  setFieldValue,
                  values,
                  ...formProps,
                })}
                disabled={locked}
              />
            </div>
            <div className="form-field">
              <Input
                type="checkbox"
                label={
                  "J’autorise Mobilité <span>Mutuelle</span> à collecter et utiliser les données personnelles nécessaires à l’exécution de mon devis et une proposition adaptée à mes besoins."
                }
                checked={get(values, "project.contact.acceptContact") || false}
                {...bindInputProps({
                  name: "project.contact.acceptContact",
                  values,
                  ...formProps,
                })}
                touched
                disabled={locked}
              />
            </div>
            <div className="form-field">
              <Input
                type="checkbox"
                label={
                  "Je dispose d’un contrat santé auprès d’un autre organisme assureur."
                }
                checked={hasContract}
                onChange={({ target: { checked } }) => {
                  setHasContract(checked);
                  setFieldValue("project.ria", checked);
                  RIA_STATUS[checked]();
                  setFieldValue("project.terminationRequest", null);
                  if (checked)
                    setFieldValue("project.fields.selfOccupation", false);
                  else
                    setFieldValue("project.fields.selfOccupation", undefined);
                }}
                disabled={locked}
              />
              {hasContract && (
                <div className="termination-container">
                  <div className="termination-container__question">
                    <Actions
                      variant={"normal"}
                      label={TRANSLATION.FORM.RIA.OLD_CONTRACT_LABEL}
                      actions={
                        <Switch
                          className="button__switch"
                          checked={get(values, "project.ria")}
                          checkedChildren={" "}
                          unCheckedChildren={" "}
                          onChange={(checked) => {
                            setFieldValue("project.ria", checked);
                            RIA_STATUS[checked]();
                            if (checked) {
                              setFieldValue(
                                "project.fields.selfOccupation",
                                false,
                              );
                            } else {
                              setFieldValue(
                                "project.fields.selfOccupation",
                                undefined,
                              );
                            }
                          }}
                        />
                      }
                    />
                    {get(values, "project.ria") && (
                      <>
                        <hr />
                        <span className="termination-container__question--title">
                          Comment souhaitez-vous gérer la résiliation de votre
                          ancien contrat ?
                        </span>
                        <Input
                          type="checkbox"
                          label={
                            "Je souhaite que <b>Mobilité Mutuelle effectue pour votre compte, la résiliation</b> de votre contrat actuellement en vigueur auprès de votre assureur."
                          }
                          checked={
                            get(values, "project.fields.selfOccupation") ===
                            false
                          }
                          onChange={() =>
                            setFieldValue(
                              "project.fields.selfOccupation",
                              false,
                            )
                          }
                        />
                        {get(values, "project.fields.selfOccupation") ===
                          false && (
                          <div className="termination-container__question--block">
                            {/* <Input
                              label={
                                TRANSLATION.FORM.CURRENT_EFFECTIVE_DATE.LABEL
                              }
                              placeholder={
                                TRANSLATION.FORM.CURRENT_EFFECTIVE_DATE
                                  .PLACEHOLDER
                              }
                              type="date"
                              variant="simple"
                              format={DATE_FORMAT}
                              defaultPickerValue={dayjs().add(-1, "day")}
                              disabledDate={(current) => {
                                return current && current > dayjs();
                              }}
                              {...bindDateInputProps({
                                name: `project.terminationRequest.dueDate`,
                                values,
                                setFieldValue,
                                ...formProps,
                              })}
                              disabled={locked}
                            /> */}
                            <Input
                              label={TRANSLATION.FORM.CURRENT_CONTRACT.LABEL}
                              placeholder={
                                TRANSLATION.FORM.CURRENT_CONTRACT.PLACEHOLDER
                              }
                              {...bindInputProps({
                                name: `project.terminationRequest.contractNumber`,
                                values,
                                setFieldValue,
                                ...formProps,
                              })}
                              onChange={({ target: { value } }) => {
                                const filteredValue =
                                  filterContractNumber(value);
                                setFieldValue(
                                  `project.terminationRequest.contractNumber`,
                                  filteredValue,
                                );
                              }}
                              onBlur={({ target: { value } }) => {
                                const trimmedValue = value.trim();
                                setFieldValue(
                                  `project.terminationRequest.contractNumber`,
                                  trimmedValue,
                                );
                                formProps.setFieldTouched(
                                  `project.terminationRequest.contractNumber`,
                                  true,
                                );
                              }}
                              disabled={locked}
                            />
                            <Input
                              label={TRANSLATION.FORM.CURRENT_COMPANY.LABEL}
                              placeholder={
                                TRANSLATION.FORM.CURRENT_COMPANY.LABEL
                              }
                              {...bindInputProps({
                                name: `project.terminationRequest.brokerageMandate.currentCompany`,
                                values,
                                setFieldValue,
                                ...formProps,
                              })}
                              onChange={({ target: { value } }) => {
                                setFieldValue(
                                  `project.terminationRequest.brokerageMandate.id`,
                                  cuid(),
                                );
                                setFieldValue(
                                  `project.terminationRequest.brokerageMandate.currentCompany`,
                                  value,
                                );
                              }}
                              onBlur={({ target: { value } }) => {
                                setFieldValue(
                                  `project.terminationRequest.brokerageMandate.id`,
                                  cuid(),
                                );
                                setFieldValue(
                                  `project.terminationRequest.brokerageMandate.currentCompany`,
                                  value,
                                );
                                formProps.setFieldTouched(
                                  `project.terminationRequest.brokerageMandate.currentCompany`,
                                  true,
                                );
                              }}
                              disabled={locked}
                            />
                            <Input
                              label={
                                TRANSLATION.FORM.CURRENT_COMPANY_ADDRESS.LABEL
                              }
                              placeholder={
                                TRANSLATION.FORM.CURRENT_COMPANY_ADDRESS
                                  .PLACEHOLDER
                              }
                              {...bindInputProps({
                                name: `project.terminationRequest.address`,
                                values,
                                setFieldValue,
                                ...formProps,
                              })}
                              onChange={({ target: { value } }) => {
                                setFieldValue(
                                  `project.terminationRequest.address`,
                                  value,
                                );
                              }}
                              onBlur={({ target: { value } }) => {
                                setFieldValue(
                                  `project.terminationRequest.address`,
                                  value,
                                );
                                formProps.setFieldTouched(
                                  `project.terminationRequest.address`,
                                  true,
                                );
                              }}
                              disabled={locked}
                            />
                          </div>
                        )}
                        <Input
                          type="checkbox"
                          label={"Je me charge de résilier moi même"}
                          checked={
                            get(values, "project.fields.selfOccupation") ===
                            true
                          }
                          onChange={() =>
                            setFieldValue("project.fields.selfOccupation", true)
                          }
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
      <ActionButtons
        disabled={!isEmpty(formProps?.errors)}
        onNext={handleSubmit}
      />
    </Layout>
  );
}

export default View;
